import { queryOptions, useQuery } from "@tanstack/react-query";
import { getOffset } from "@package/grid-utils";
import { atomWithStorage } from "jotai/utils";
import { logger } from "@artis/logger";
import { useAtom } from "jotai";
import { useMemo } from "react";

import { debugAtomStore, debugOptionsAtom, readInitialDebugOptions } from "../main/debug";
import type { StorageType } from "../lib/domain";
import { isMobile } from "../../../shared/hooks";
import { createCalcClient } from "../main";
import { getWsStats } from "@package/graphql";

const debugOptions = readInitialDebugOptions();
const isSharedWorker =
  !isMobile &&
  typeof SharedWorker !== "undefined" &&
  typeof (window as unknown as { Office: unknown }).Office === "undefined";
const worker = createCalcClient({ isSharedWorker, debug: debugOptions });

setInterval(() => logger.info("worker-api-performance", { stats: worker.stats.stats(), ws: getWsStats() }), 60 * 1000);

const tabId = crypto.randomUUID().slice(0, 8);
document.addEventListener("freeze", () => logger.info("tab-frozen", { tabId }));
document.addEventListener("resume", () => logger.info("tab-resumed", { tabId }));

export const useCalcWorker = () => worker;

/** Tell the worker this tab just saved these curves, so it does not notify the editor. */
export const expectOwnProductChanges = (productIds: Iterable<string>) => {
  const ids = [...new Set([...productIds].filter(Boolean))];
  if (ids.length === 0) return;
  worker.send({ type: "expect-product-changes", data: { productIds: ids, at: Date.now() } });
};

export const useCalcs = () => {
  return useMemo(() => {
    return {
      send: worker.send,
      cellEdit: worker.cellEdit,
      getGrid: (...args: Parameters<typeof worker.getGrid>) => Promise.resolve(worker.getGrid(...args)),
      getCell: (...args: Parameters<typeof worker.getCell>) => Promise.resolve(worker.getCell(...args)),
      get getManualCell() {
        return worker.api.getManualCell;
      },
      get getLatestLivePrice() {
        return worker.api.getLatestLivePrice;
      },
      get getPriorEodDate() {
        return worker.api.getPriorEodDate;
      },
      get getEditStatus() {
        return worker.api.getEditStatus;
      },
      get debugGetCachedProductValues() {
        return worker.api.debugGetCachedProductValues;
      },
    };
  }, []);
};

export type Calcs = ReturnType<typeof useCalcs>;

const paused = atomWithStorage<boolean>("worker-paused", false);
export const usePauseCalcs = () => useAtom(paused);

const useEditStatusQuery = (product?: string | null, type?: StorageType | null) => {
  const calcs = useCalcs();
  return queryOptions({
    queryKey: ["calcs", "editStatus", product, type],
    enabled: !!product && !!type,
    refetchInterval: 1000,
    queryFn: () => {
      if (!worker || !product || !type) return null;
      return Promise.resolve(calcs.getEditStatus(type, product)).then((x) => x ?? null);
    },
  });
};

const maybeDate = (date: string | undefined | null) => (date ? new Date(date).getTime() : 0);

export const useProductEditStatus = (product?: string | null, type?: StorageType | null) =>
  useQuery({
    ...useEditStatusQuery(product, type),
    select: (data) => {
      return Object.values(data ?? {})
        .sort((a, b) => maybeDate(b?.edited_at) - maybeDate(a?.edited_at))
        .at(0);
    },
  });

export const useCellEditStatus = (product?: string | null, type?: StorageType | null, row?: string | null) =>
  useQuery({
    ...useEditStatusQuery(product, type),
    select: (data) => {
      const offset = row !== null && row !== undefined ? getOffset(row) : undefined;
      return typeof offset === "number" ? data?.[offset] : null;
    },
  });

export const useManualCellValue = (product?: string | null, type?: StorageType | null, row?: string | null) => {
  const calcs = useCalcs();
  return useQuery({
    queryKey: ["calcs", "manualCellValue", product, type, row],
    enabled: !!product && !!type && !!row,
    refetchInterval: 1000,
    queryFn: () => {
      if (!worker || !product || !type || !row) return null;
      const offset = getOffset(row);
      if (typeof offset !== "number") return null;
      return Promise.resolve(calcs.getManualCell(type, product, offset)).then((x) => x ?? null);
    },
  });
};

export const useDebugOptions = () => useAtom(debugOptionsAtom, { store: debugAtomStore });
